import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {NewsCluster} from "../types/types";
import Collapse from '@mui/material/Collapse'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Badge,
    Box,
    Button,
    Checkbox,
    Chip,
    Drawer,
    Fab,
    FormControlLabel,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tab,
    Tabs,
    TextField,
    Tooltip as MuiTooltip,
    Typography,
} from '@mui/material';
import {
    ArrowDropUp,
    ChevronLeft,
    ChevronRight,
    Close as CloseIcon,
    ExpandMore,
    FilterList,
    Search,
    ThumbDownAlt as ThumbDownAltIcon,
    ThumbUpAlt as ThumbUpAltIcon,
    UnfoldMore,
    ArrowDropDown,
    Visibility as VisibilityIcon,
    Save as SaveIcon,
} from '@mui/icons-material';

import {format} from 'date-fns';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {SentimentHistogram} from "../components/sentiment/SentimentHistogram";
import {api} from "../api/apiService";
import { NotebooksContext } from '../contexts/NotebooksContext';
import ClusterRadarDetailPanel from '../components/ClusterRadarDetailPanel';
import {RadarArticleTypeChipsRow} from '../components/radar/RadarTableCells';
import { AnalyticProfileContext, AnalyticProfileContextType } from '../contexts/AnalyticProfileContext';
import { formatCountryFieldAsIso } from '../utils/countryIso';
import { NotebookPickerList } from '../components/notebook/NotebookPickerList';
import { getClusterMaxPublishedAt } from '../utils/clusterArticles';
import { radarLookbackDaysFromProfile } from '../utils/radarLookback';
import { useRadarCluster, useRadarPage } from '../data/useRadarPage';
import { parseAppLocation, radarClusterPath, radarPath } from '../routing/appRoutes';
import {
    ARTICLE_COUNT_BUCKETS,
    ArticleCountBucket,
    GeoScope,
    nextSort,
    parseClustersFilters,
    serializeClustersFilters,
    toggleValue,
    useRadarSearchInput,
    useRadarUrlFilters,
} from '../routing/radarUrlState';

// Maximum number of clusters that can be saved to a notebook at once
const MAX_CLUSTERS_PER_SAVE = 10;

// For sorting
type SortOrder = 'asc' | 'desc' | null;
type SortField = 'article_count' | 'country' | 'published_at' | null;
type RadarDrawerMode = 'closed' | 'filters' | 'details';

/** URL id of a cluster (/radar/clusters/:clusterId); title is the legacy fallback key used across this view. */
const clusterUrlId = (cluster: NewsCluster) => cluster.cluster_id ?? cluster.title;

/** history.state marker: this cluster entry was pushed from the list, so closing can go Back to it. */
type ClusterLocationState = { radarClusterFromList?: boolean } | null;

export type LayoutNewsClustersProps = {
    /** From Library: pre-select this notebook when user opens Save to notebook. */
    preselectNotebookIdForSave?: string | null;
    onConsumedPreselectNotebookIdForSave?: () => void;
};

function clusterKeywords(cluster: NewsCluster): string[] {
    const raw = cluster.keywords;
    if (Array.isArray(raw)) {
        return raw.map((word) => word.toLowerCase().trim()).filter(Boolean);
    }
    if (typeof raw === 'string') {
        return raw.split(',').map((word) => word.toLowerCase().trim()).filter(Boolean);
    }
    return [];
}

const LayoutNewsClusters = ({
    preselectNotebookIdForSave = null,
    onConsumedPreselectNotebookIdForSave,
}: LayoutNewsClustersProps) => {
    // Filters, sort and page live in the query string; the open cluster is the path segment.
    // Discrete changes push a history entry (Back undoes them); a search pushes once, then refines in place.
    const { filters, update, location, navigate } = useRadarUrlFilters(parseClustersFilters, serializeClustersFilters);
    const routeClusterId = useMemo(() => parseAppLocation(location.pathname)?.itemId ?? null, [location.pathname]);
    const listPath = radarPath('clusters');
    const setUrlQuery = useCallback((q: string, opts: { replace: boolean }) => update({ q }, opts), [update]);
    const [searchTerm, setSearchTerm] = useRadarSearchInput(filters.q, setUrlQuery);
    const {
        countries: countryFilter,
        geoScopes: geoScopeFilter,
        recommended: recommendedFilter,
        articleCounts: articleCountFilter,
        entryTypes: entryTypeFilter,
        issueTypes: issueTypeFilter,
        contentCategories: contentCategoryFilter,
        page: currentPage,
    } = filters;
    const setCountryFilter = (countries: string[]) => update({ countries });
    const setEntryTypeFilter = (entryTypes: string[]) => update({ entryTypes });
    const setIssueTypeFilter = (issueTypes: string[]) => update({ issueTypes });
    const setContentCategoryFilter = (contentCategories: string[]) => update({ contentCategories });
    // Recommended / article-count are applied client-side to the current page, so they keep the page.
    const setRecommendedFilter = (recommended: boolean) => update({ recommended }, { keepPage: true });
    const setArticleCountFilter = (articleCounts: ArticleCountBucket[]) => update({ articleCounts }, { keepPage: true });
    const setCurrentPage = (page: number) => update({ page });

    const [countrySearchTerm, setCountrySearchTerm] = useState('');
    const [entryTypeSearchTerm, setEntryTypeSearchTerm] = useState('');
    const [issueTypeSearchTerm, setIssueTypeSearchTerm] = useState('');
    const [contentCategorySearchTerm, setContentCategorySearchTerm] = useState('');
    const [selectedClusters, setSelectedClusters] = useState<NewsCluster[]>([]); // Track selected clusters

    /** Display count: unique articles when present, so it matches the number of articles shown. */
    const getDisplayArticleCount = (cluster: NewsCluster): number =>
        (cluster as { unique_article_count?: number }).unique_article_count ?? cluster.article_count;

    // Helper function to get cluster types
    const getClusterTypes = (cluster: NewsCluster): string[] => {
        const types: string[] = [];
        if (cluster.cluster_entry_type) types.push(cluster.cluster_entry_type);
        if (cluster.cluster_content_category) types.push(cluster.cluster_content_category);
        // Always add issue_type, show "N/A" if empty
        types.push(cluster.cluster_issue_type || "N/A");
        return types;
    };

    const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
    const [missingClusterToastOpen, setMissingClusterToastOpen] = useState(false);
    /** The cluster shown for a URL id (clicked in the list or fetched by id), so the drawer stays put while
     *  the page refetches, fails in the background, or the by-id query gets disabled. */
    const openedClusterRef = useRef<{ id: string; lookbackDays: number; cluster: NewsCluster } | null>(null);

    // Cluster feedback states (thumbs up/down)
    const [clusterFeedbackError, setClusterFeedbackError] = useState('');
    const [clusterFeedbackSuccess, setClusterFeedbackSuccess] = useState('');
    const [showClusterFeedbackAlert, setShowClusterFeedbackAlert] = useState(false);

    // Incorrect articles states
    const [articleFeedbackError, setArticleFeedbackError] = useState('');
    const [articleFeedbackSuccess, setArticleFeedbackSuccess] = useState('');
    const [incorrectArticles, setIncorrectArticles] = useState<string[]>([]);
    const [showArticleFeedbackAlert, setShowArticleFeedbackAlert] = useState(false);

    // Sorting
    const sortField: SortField = filters.sort?.field ?? null;
    const sortOrder: SortOrder = filters.sort?.order ?? null;
    const [likedClusters, setLikedClusters] = useState<string[]>([]);
    const [dislikedClusters, setDislikedClusters] = useState<string[]>([]);
    const [hiddenClusterKeys, setHiddenClusterKeys] = useState<string[]>([]);
    const [preferredKeywords, setPreferredKeywords] = useState<string[]>([]);

    const itemsPerPage = 10;

    // Helper to get the right icon based on sorting state
    const getSortIcon = (field: SortField) => {
        if (sortField === field && sortOrder === 'asc') {
            return <ArrowDropUp fontSize="small" style={{ marginLeft: '4px' }} />;
        } else if (sortField === field && sortOrder === 'desc') {
            return <ArrowDropDown fontSize="small" style={{ marginLeft: '4px' }} />;
        } else {
            // Neutral icon if not sorted, or sorted on another field
            return <UnfoldMore fontSize="small" style={{ marginLeft: '4px' }} />;
        }
    };

    // Third-click logic: asc → desc → not sorted anymore
    const handleSort = (field: SortField) => {
        if (field) update({ sort: nextSort(filters.sort, field) });
    };

    const { selectedProfileId, selectedProfile, selectionRevision } = useContext<AnalyticProfileContextType>(AnalyticProfileContext);

    const radarLookbackDays = useMemo(
        () => radarLookbackDaysFromProfile(selectedProfile),
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps: only time_window_hours matters, profile object identity may change
        [selectedProfile?.id, selectedProfile?.time_window_hours],
    );

    // Closing a cluster that was opened from the list goes Back to that list entry (no duplicate).
    // The idx guard covers odd session restores that keep the state flag but lose the earlier entry.
    const canGoBackToList = () =>
        Boolean((location.state as ClusterLocationState)?.radarClusterFromList) &&
        ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
    const backToList = () => {
        if (canGoBackToList()) navigate(-1);
        else navigate({ pathname: listPath, search: location.search }, { replace: true });
    };

    // An explicit analytic profile switch keeps the filters but returns to page 1 and closes the open
    // cluster, since both belong to the previous result set. selectionRevision only moves on a user
    // choice, so the default pick or the localStorage restore on login doesn't count and deep links survive.
    const prevSelectionRevisionRef = useRef(selectionRevision);
    useEffect(() => {
        if (prevSelectionRevisionRef.current === selectionRevision) return;
        prevSelectionRevisionRef.current = selectionRevision;
        openedClusterRef.current = null;
        // Opened from the list on page 1: the list entry already shows the result we want, so go Back to it.
        if (routeClusterId && currentPage === 1 && canGoBackToList()) navigate(-1);
        else update({ page: 1 }, { replace: true, pathname: listPath });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- react to explicit profile switches only
    }, [selectionRevision]);

    const radarPage = useRadarPage({
        profileId: selectedProfileId ?? null,
        types: ['clustered'],
        page: currentPage,
        pageSize: itemsPerPage,
        q: filters.q || undefined,
        countries: countryFilter.length ? countryFilter : undefined,
        geoScopes: geoScopeFilter.length ? geoScopeFilter : undefined,
        entryTypes: entryTypeFilter.length ? entryTypeFilter : undefined,
        issueTypes: issueTypeFilter.length ? issueTypeFilter : undefined,
        contentCategories: contentCategoryFilter.length ? contentCategoryFilter : undefined,
        sort: filters.sort?.field ?? null,
        order: filters.sort?.order ?? null,
        lookbackDays: radarLookbackDays,
    });

    const clusters = radarPage.items;
    /** Page of the current result set has loaded successfully (not loading, not failed). */
    const radarPageSettled =
        Boolean(selectedProfileId) && radarPage.hasData && !radarPage.isLoading && !radarPage.error;

    // Not memoized: it reads openedClusterRef, and the scan is over one page (10 rows).
    const clusterOnPage = (() => {
        if (!routeClusterId) return null;
        const found = clusters.find((c) => clusterUrlId(c) === routeClusterId);
        if (found) return found;
        const opened = openedClusterRef.current;
        // A lookback change re-validates via the page / by-id query instead of keeping a stale pin.
        return opened && opened.id === routeClusterId && opened.lookbackDays === radarLookbackDays ? opened.cluster : null;
    })();
    // Not on the linked page (newer clusters can push it to a later one): fetch it by id. The result is
    // keyed by the URL id, so it's trusted even if the payload's cluster_id is missing or formatted differently.
    // Title-based ids containing '/' would become %2F in the path, which proxies may reject. Those
    // skip the lookup and are reported missing.
    const canFetchById = !!routeClusterId && !routeClusterId.includes('/');
    const clusterById = useRadarCluster(
        routeClusterId,
        selectedProfileId ?? null,
        radarLookbackDays,
        canFetchById && !clusterOnPage && radarPageSettled,
    );
    const clusterMissing = clusterById.notFound || (!canFetchById && radarPageSettled);
    const selectedClusterForDetails = clusterOnPage ?? clusterById.cluster;
    // Deliberate, idempotent ref write during render (same id-keyed query data on every pass). Moving it
    // into an effect would reintroduce a frame where the drawer is closed.
    if (routeClusterId && !clusterOnPage && clusterById.cluster) {
        openedClusterRef.current = { id: routeClusterId, lookbackDays: radarLookbackDays, cluster: clusterById.cluster };
    }

    // A by-id lookup that failed for another reason (network, 5xx) keeps the URL for a retry; say so.
    const [clusterLoadFailedToastOpen, setClusterLoadFailedToastOpen] = useState(false);
    useEffect(() => {
        if (clusterById.failed) setClusterLoadFailedToastOpen(true);
    }, [clusterById.failed]);
    const radarDrawerMode: RadarDrawerMode = filtersDrawerOpen
        ? 'filters'
        : selectedClusterForDetails
            ? 'details'
            : 'closed';

    // Deep link to a cluster the server says doesn't exist (other profile, aged out of the window): say so
    // and drop it from the URL, keeping the filters. A failed fetch (page or by-id) keeps the URL so a
    // reload can retry.
    useEffect(() => {
        if (!routeClusterId || selectedClusterForDetails || !clusterMissing) return;
        setMissingClusterToastOpen(true);
        navigate({ pathname: listPath, search: location.search }, { replace: true });
    }, [routeClusterId, selectedClusterForDetails, clusterMissing, navigate, listPath, location.search]);

    // A page past the end (shared link after the result set shrank) snaps to the last page.
    useEffect(() => {
        if (routeClusterId || !radarPageSettled || radarPage.total === 0) return;
        if (currentPage > radarPage.pageCount) update({ page: radarPage.pageCount }, { replace: true });
    }, [routeClusterId, radarPageSettled, radarPage.total, radarPage.pageCount, currentPage, update]);

    // Surface fetch errors through console.error to preserve previous behaviour.
    // Real error reporting would funnel into Sentry/Datadog here.
    useEffect(() => {
        if (radarPage.error) {
            // eslint-disable-next-line no-console
            console.error("Error fetching news clusters:", radarPage.error);
        }
    }, [radarPage.error]);

    const allCountries = useMemo(() => {
        const countriesSet = new Set<string>();
        clusters.forEach((cluster) => {
            cluster.country
                .split(',')
                .map((country) => country.trim())
                .forEach((country) => {
                    countriesSet.add(country);
                });
        });
        return Array.from(countriesSet).sort((a, b) =>
            a.localeCompare(b, 'sl', { sensitivity: 'base' })
        );
    }, [clusters]);

    /** Same membership as the former “Recommended clusters” view: first three clustered rows per scope. */
    const recommendedClusterKeys = useMemo(() => {
        const local = clusters
            .filter((c) => (c.country_relationship || 'international') === 'local')
            .slice(0, 3);
        const regional = clusters
            .filter((c) => (c.country_relationship || 'international') === 'regional')
            .slice(0, 3);
        const international = clusters
            .filter((c) => (c.country_relationship || 'international') === 'international')
            .slice(0, 3);
        const keys = new Set<string>();
        [...local, ...regional, ...international].forEach((c) => {
            keys.add(c.cluster_id ?? c.title);
        });
        return keys;
    }, [clusters]);

    const filteredCountries = useMemo(() => {
        return allCountries
            .filter((country) => country.toLowerCase().includes(countrySearchTerm.toLowerCase()))
            .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [allCountries, countrySearchTerm]);

    // Type filter options
    const allEntryTypes = useMemo(() => {
        const typesSet = new Set<string>();
        clusters.forEach((cluster) => {
            if (cluster.cluster_entry_type) typesSet.add(cluster.cluster_entry_type);
        });
        return Array.from(typesSet).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [clusters]);

    const filteredEntryTypes = useMemo(() => {
        return allEntryTypes
            .filter((type) => type.toLowerCase().includes(entryTypeSearchTerm.toLowerCase()))
            .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [allEntryTypes, entryTypeSearchTerm]);

    const allIssueTypes = useMemo(() => {
        const typesSet = new Set<string>();
        clusters.forEach((cluster) => {
            if (cluster.cluster_issue_type) typesSet.add(cluster.cluster_issue_type);
        });
        return Array.from(typesSet).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [clusters]);

    const filteredIssueTypes = useMemo(() => {
        return allIssueTypes
            .filter((type) => type.toLowerCase().includes(issueTypeSearchTerm.toLowerCase()))
            .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [allIssueTypes, issueTypeSearchTerm]);

    const allContentCategories = useMemo(() => {
        const categoriesSet = new Set<string>();
        clusters.forEach((cluster) => {
            if (cluster.cluster_content_category) categoriesSet.add(cluster.cluster_content_category);
        });
        return Array.from(categoriesSet).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [clusters]);

    const filteredContentCategories = useMemo(() => {
        return allContentCategories
            .filter((category) => category.toLowerCase().includes(contentCategorySearchTerm.toLowerCase()))
            .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
    }, [allContentCategories, contentCategorySearchTerm]);

    // Article count options
    const articleCountOptions = ARTICLE_COUNT_BUCKETS;

    /*
     * Server handles search, country, geo, entry, issue, category, sort, and page.
     * Accept and dismiss also change this page immediately: a dismissed cluster leaves,
     * and a cluster that shares a keyword with an accepted one moves up.
     * Article-count and recommended filters stay client-side.
     */
    const filteredClusters = useMemo(() => {
        const preferred = new Set(preferredKeywords);
        const hidden = new Set(hiddenClusterKeys);
        const visible = clusters.filter((cluster) => {
            const key = cluster.cluster_id ?? cluster.title;
            return !hidden.has(key) && !hidden.has(cluster.title);
        });
        const ordered = preferred.size === 0 ? visible : [...visible].sort((a, b) => {
            const score = (cluster: NewsCluster) => clusterKeywords(cluster).some((word) => preferred.has(word)) ? 1 : 0;
            return score(b) - score(a);
        });
        return ordered.filter((cluster) => {
            const clusterKey = cluster.cluster_id ?? cluster.title;
            const matchesRecommended = !recommendedFilter || recommendedClusterKeys.has(clusterKey);

            const matchesArticleCount =
                articleCountFilter.length === 0 ||
                articleCountFilter.some((range) => {
                    const articleCount = getDisplayArticleCount(cluster);
                    switch (range) {
                        case '<5':
                            return articleCount < 5;
                        case '5-9':
                            return articleCount >= 5 && articleCount <= 9;
                        case '10-14':
                            return articleCount >= 10 && articleCount <= 14;
                        case '15+':
                            return articleCount >= 15;
                        default:
                            return false;
                    }
                });

            return matchesRecommended && matchesArticleCount;
        });
    }, [clusters, recommendedFilter, recommendedClusterKeys, articleCountFilter, hiddenClusterKeys, preferredKeywords]);

    // Server already returned the page sorted; expose under the historic
    // names so the JSX below didn't have to be rewritten.
    const sortedClusters = filteredClusters;
    const displayedClusters = filteredClusters;

    const formatDate = (dateString?: string) => {
        if (!dateString) {
            return '-';
        }
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
            return '-';
        }
        return format(date, 'dd/MM/yyyy');
    };

    // Handle selecting clusters
    const handleClusterSelect = (cluster: NewsCluster) => {
        const isSelected = selectedClusters.some((selected) => selected.title === cluster.title);
        if (isSelected) {
            setSelectedClusters(selectedClusters.filter((selected) => selected.title !== cluster.title));
        } else {
            setSelectedClusters([...selectedClusters, cluster]);
        }
    };

    // Handler for "Select All" checkbox
    const handleSelectAllClusters = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedClusters(sortedClusters);
        } else {
            setSelectedClusters([]);
        }
    };

    const closeRadarDrawer = () => {
        if (filtersDrawerOpen) {
            setFiltersDrawerOpen(false);
            return;
        }
        if (!routeClusterId) return;
        // Opened from the list → go Back to it (no duplicate list entry); opened from a link → replace.
        backToList();
    };

    const openFiltersDrawer = () => {
        setFiltersDrawerOpen(true);
    };

    const openClusterDetailsDrawer = (cluster: NewsCluster) => {
        openedClusterRef.current = { id: clusterUrlId(cluster), lookbackDays: radarLookbackDays, cluster };
        const state: ClusterLocationState = { radarClusterFromList: true };
        navigate({ pathname: radarClusterPath(clusterUrlId(cluster)), search: location.search }, { state });
    };

    const handleCountryToggle = (country: string) => {
        const currentIndex = countryFilter.indexOf(country);
        const newChecked = [...countryFilter];

        if (currentIndex === -1) {
            newChecked.push(country);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setCountryFilter(newChecked);
    };

    const handleGeoScopeToggle = (scope: GeoScope) => {
        update({ geoScopes: toggleValue(geoScopeFilter, scope) });
    };

    const handleArticleCountToggle = (range: ArticleCountBucket) => {
        setArticleCountFilter(toggleValue(articleCountFilter, range));
    };

    const handleEntryTypeToggle = (entryType: string) => {
        const currentIndex = entryTypeFilter.indexOf(entryType);
        const newChecked = [...entryTypeFilter];

        if (currentIndex === -1) {
            newChecked.push(entryType);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setEntryTypeFilter(newChecked);
    };

    const handleIssueTypeToggle = (issueType: string) => {
        const currentIndex = issueTypeFilter.indexOf(issueType);
        const newChecked = [...issueTypeFilter];

        if (currentIndex === -1) {
            newChecked.push(issueType);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setIssueTypeFilter(newChecked);
    };

    const handleContentCategoryToggle = (contentCategory: string) => {
        const currentIndex = contentCategoryFilter.indexOf(contentCategory);
        const newChecked = [...contentCategoryFilter];

        if (currentIndex === -1) {
            newChecked.push(contentCategory);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setContentCategoryFilter(newChecked);
    };

    // Handle cluster feedback (Thumbs up/down)
    const handleClusterFeedback = (cluster: NewsCluster, positive: boolean) => {
        setClusterFeedbackError('');
        setClusterFeedbackSuccess('');
        const key = cluster.cluster_id || cluster.title;
        const keywords = clusterKeywords(cluster);
        api.post('/api/feedback/clusters/mismatch', {
            cluster_id: key,
            positive: positive,
            keywords,
        }).then(() => {
            if (positive) {
                setPreferredKeywords((prev) => Array.from(new Set([...prev, ...keywords])));
            } else {
                setHiddenClusterKeys((prev) => prev.includes(key) ? prev : [...prev, key]);
            }
            setClusterFeedbackSuccess(positive
                ? 'Accepted. Clusters that share a keyword move up.'
                : 'Dismissed. This cluster leaves the list.');
            radarPage.refetch();
        }).catch((error: { message?: string }) => {
            setClusterFeedbackError(error.message || 'An unexpected error occurred.');
        }).finally(() => {
            setShowClusterFeedbackAlert(true);
        });
    };

    // Incorrect articles states
    const handleArticleFeedback = async (clusterId: string, articleUrl: string) => {
        setArticleFeedbackError('');
        setArticleFeedbackSuccess('');

        // Toggle first so the user sees the color change right away:
        const isIncorrect = incorrectArticles.includes(articleUrl);
        setIncorrectArticles((prev) =>
            isIncorrect
                ? prev.filter((url) => url !== articleUrl)
                : [...prev, articleUrl]
        );

        // Then attempt your request
        try {
            api.post('/api/feedback/articles/mismatch', {
                cluster_id: clusterId,
                article_url: articleUrl,
                mismatch: !isIncorrect
            }).then(res => {
                setArticleFeedbackSuccess('Feedback submitted successfully!');
                setShowArticleFeedbackAlert(true);
            })
        } catch (error: any) {
            setArticleFeedbackError(error.message || 'An unexpected error occurred.');
            setShowArticleFeedbackAlert(true);
        }
    };

    // Get notebooks functionality from context
    const { 
        notebooks: notebooksFromContext, 
        isLoading: notebooksLoading, 
        error: notebooksError, 
        addNotebook: createNotebook, 
        addClustersToNotebook: addClustersToNotebookFn, 
        getActiveNotebooks: getActiveNotebooksFn,
        fetchNotebooks,
    } = useContext(NotebooksContext);

    const [saveToNotebookDrawerOpen, setSaveToNotebookDrawerOpen] = useState<boolean>(false);
    const [saveNotebookTab, setSaveNotebookTab] = useState(0);
    const [selectedNotebookId, setSelectedNotebookId] = useState<string>('');
    const [newNotebookName, setNewNotebookName] = useState<string>('');
    const [newNotebookDescription, setNewNotebookDescription] = useState<string>('');
    const [saveToNotebookSuccess, setSaveToNotebookSuccess] = useState<string>('');
    const [saveToNotebookError, setSaveToNotebookError] = useState<string>('');
    const [showSaveToNotebookAlert, setShowSaveToNotebookAlert] = useState<boolean>(false);
    const [isSavingToNotebook, setIsSavingToNotebook] = useState<boolean>(false);

    const activeNotebooks = useMemo(() => {
        return getActiveNotebooksFn();
    }, [getActiveNotebooksFn]);

    const closeSaveToNotebookDrawer = () => {
        setSaveToNotebookDrawerOpen(false);
        setSaveNotebookTab(0);
        setNewNotebookName('');
        setNewNotebookDescription('');
        setSelectedNotebookId('');
    };

    const openSaveToNotebookDrawer = () => {
        setSaveNotebookTab(0);
        setSaveToNotebookError('');
        setSaveToNotebookSuccess('');
        setSaveToNotebookDrawerOpen(true);
        void fetchNotebooks();
    };

    // Handle saving clusters to notebook
    const handleSaveToNotebook = async () => {
        setSaveToNotebookError('');
        setSaveToNotebookSuccess('');
        setIsSavingToNotebook(true);

        try {
            if (selectedClusters.length === 0) {
                setSaveToNotebookError('No clusters selected to save');
                setShowSaveToNotebookAlert(true);
                return;
            }

            // Limit the number of clusters that can be added at once
            if (selectedClusters.length > MAX_CLUSTERS_PER_SAVE) {
                setSaveToNotebookError(`You can only add up to ${MAX_CLUSTERS_PER_SAVE} clusters to a notebook at once. Please select fewer clusters.`);
                setShowSaveToNotebookAlert(true);
                return;
            }

            const missingClusterIdCount = selectedClusters.filter((c) => !(c.cluster_id || '').trim()).length;
            if (missingClusterIdCount > 0) {
                setSaveToNotebookError(
                    `${missingClusterIdCount} selected cluster(s) are missing cluster_id, so they cannot be saved by reference.`
                );
                setShowSaveToNotebookAlert(true);
                return;
            }

            if (saveNotebookTab === 1) {
                const trimmedNotebookName = newNotebookName.trim();
                if (!trimmedNotebookName) {
                    setSaveToNotebookError('Please enter a name for the new notebook');
                    setShowSaveToNotebookAlert(true);
                    return;
                }

                // Create notebook first, then add clusters in a second call.
                // This avoids backend create-schema failures when clusters are embedded in POST /api/notebooks.
                const newNotebook = await createNotebook({
                    name: trimmedNotebookName,
                    description: newNotebookDescription.trim() || 'Created from News clusters',
                    clusters: [],
                    status: 'active',
                    visibility: 'private'
                });

                if (newNotebook?.id) {
                    const addOk = await addClustersToNotebookFn(newNotebook.id, selectedClusters);
                    if (!addOk) {
                        setSaveToNotebookError(notebooksError || 'Notebook created, but failed to add clusters');
                        setShowSaveToNotebookAlert(true);
                        return;
                    }
                    setSaveToNotebookSuccess(`Clusters saved to new notebook "${trimmedNotebookName}" successfully`);
                } else {
                    setSaveToNotebookError(notebooksError || 'Failed to create new notebook');
                }
            } else {
                if (!selectedNotebookId) {
                    setSaveToNotebookError('Please select a notebook');
                    setShowSaveToNotebookAlert(true);
                    return;
                }

                // Find the selected notebook
                const notebook = notebooksFromContext.find(n => n.id === selectedNotebookId);
                if (!notebook) {
                    setSaveToNotebookError('Selected notebook not found');
                    setShowSaveToNotebookAlert(true);
                    return;
                }

                // Add clusters to the existing notebook
                const success = await addClustersToNotebookFn(selectedNotebookId, selectedClusters);
                
                if (success) {
                    // Reset form and show success message
                    setSaveToNotebookSuccess(`Clusters saved to notebook "${notebook.name}" successfully`);
                } else {
                    setSaveToNotebookError('Failed to add clusters to the notebook');
                }
            }

            setShowSaveToNotebookAlert(true);
            closeSaveToNotebookDrawer();
        } catch (error: any) {
            console.error('Error saving to notebook:', error);
            setSaveToNotebookError(error.message || 'An error occurred while saving to notebook');
            setShowSaveToNotebookAlert(true);
        } finally {
            setIsSavingToNotebook(false);
        }
    };

    useEffect(() => {
        if (!preselectNotebookIdForSave || !onConsumedPreselectNotebookIdForSave) return;

        const nb = notebooksFromContext.find((n) => n.id === preselectNotebookIdForSave);
        if (!nb) {
            if (!notebooksLoading) {
                setSaveToNotebookError('That draft was not found among active notebooks.');
                setSaveToNotebookSuccess('');
                setShowSaveToNotebookAlert(true);
                onConsumedPreselectNotebookIdForSave();
            }
            return;
        }

        setSelectedNotebookId(preselectNotebookIdForSave);
        setSaveNotebookTab(0);
        setSaveToNotebookError('');
        setSaveToNotebookSuccess(
            'This draft is pre-selected in Save to notebook. Select clusters on the map, then use Save to notebook.'
        );
        setShowSaveToNotebookAlert(true);
        onConsumedPreselectNotebookIdForSave();
    }, [
        preselectNotebookIdForSave,
        notebooksFromContext,
        notebooksLoading,
        onConsumedPreselectNotebookIdForSave,
    ]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="#0a0a0a">
                {/* Header: search + filter */}
                <Box borderBottom={1} borderColor="#27272a">
                    <Box display="flex" alignItems="center" padding={2} gap={1}>
                        <TextField
                            variant="outlined"
                            placeholder="Search clusters..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: '#a1a1aa' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                maxWidth: 720,
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#18181b',
                                    color: '#fafafa',
                                    '& fieldset': { borderColor: '#3f3f46' },
                                    '&:hover fieldset': { borderColor: '#a1a1aa' },
                                    '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
                                },
                                '& .MuiInputBase-input::placeholder': { color: '#a1a1aa', opacity: 1 },
                            }}
                        />
                        <Badge
                            color="primary"
                            variant="dot"
                            invisible={
                                !recommendedFilter &&
                                articleCountFilter.length === 0 &&
                                countryFilter.length === 0 &&
                                geoScopeFilter.length === 0 &&
                                entryTypeFilter.length === 0 &&
                                contentCategoryFilter.length === 0 &&
                                issueTypeFilter.length === 0
                            }
                        >
                            <MuiTooltip title="Filters">
                                <IconButton
                                    onClick={openFiltersDrawer}
                                    aria-label="Open filters"
                                    sx={{
                                        color: '#fafafa',
                                        border: '1px solid #3f3f46',
                                        borderRadius: 1,
                                        '&:hover': { bgcolor: '#27272a', borderColor: '#a1a1aa' },
                                    }}
                                >
                                    <FilterList />
                                </IconButton>
                            </MuiTooltip>
                        </Badge>
                    </Box>
                </Box>

                {/* Main Content */}
                <Box flexGrow={1} padding={1} sx={{ pb: 12 }}>
                    <TableContainer component={Paper} sx={{ bgcolor: '#18181b', border: '1px solid #27272a' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#09090b', borderBottom: '2px solid #27272a' }}>
                                    {/* Updated Table Header Cell with "Select All" Checkbox */}
                                    <TableCell align="center" sx={{fontWeight: 'bold', width: '3%', color: '#fafafa', borderColor: '#27272a'}}>
                                        <Checkbox
                                            indeterminate={
                                                selectedClusters.length > 0 && selectedClusters.length < sortedClusters.length
                                            }
                                            checked={
                                                sortedClusters.length > 0 && selectedClusters.length === sortedClusters.length
                                            }
                                            onChange={handleSelectAllClusters}
                                            sx={{
                                                color: '#a1a1aa',
                                                '&.Mui-checked': { color: '#60a5fa' },
                                                '&.MuiCheckbox-indeterminate': { color: '#60a5fa' }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '4%', cursor: 'pointer', color: '#fafafa', borderColor: '#27272a' }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            onClick={() => handleSort('article_count')}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#fafafa' }}>
                                                #
                                            </Typography>
                                            {getSortIcon('article_count')}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '28%', color: '#fafafa', borderColor: '#27272a' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#fafafa' }}>
                                            General overview
                                        </Typography>
                                    </TableCell>
                                    {/* Sortable Country */}
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '8%', cursor: 'pointer', color: '#fafafa', borderColor: '#27272a' }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            onClick={() => handleSort('country')}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#fafafa' }}>
                                                Country
                                            </Typography>
                                            {getSortIcon('country')}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '34%', color: '#fafafa', borderColor: '#27272a' }}>
                                        <MuiTooltip
                                            title="Format (blue) = content type · Category (purple) = editorial category · SDG (green) = Sustainable Development Goal tag"
                                            arrow
                                            placement="top"
                                        >
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#fafafa', cursor: 'help', borderBottom: '1px dotted #71717a' }}
                                            >
                                                Article types
                                            </Typography>
                                        </MuiTooltip>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '13%', color: '#fafafa', borderColor: '#27272a' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#fafafa' }}>
                                            Sentiment score
                                        </Typography>
                                    </TableCell>
                                    {/* Sortable Date */}
                                    <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%', cursor: 'pointer', color: '#fafafa', borderColor: '#27272a' }}>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            onClick={() => handleSort('published_at')}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', color: '#fafafa' }}>
                                                Date
                                            </Typography>
                                            {getSortIcon('published_at')}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{width: '3%', borderColor: '#27272a'}}/>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {displayedClusters.map((cluster, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            backgroundColor: index % 2 === 0 ? '#09090b' : '#18181b',
                                            '&:hover': {
                                                backgroundColor: '#27272a'
                                            }
                                        }}
                                    >
                                        <TableCell align="center" sx={{width: '5%', borderColor: '#27272a'}}>
                                            <Checkbox
                                                checked={selectedClusters.some((selected) => selected.title === cluster.title)}
                                                onChange={() => handleClusterSelect(cluster)}
                                                sx={{
                                                    color: '#a1a1aa',
                                                    '&.Mui-checked': { color: '#60a5fa' }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '4%', borderColor: '#27272a', color: '#fafafa' }}>
                                            {getDisplayArticleCount(cluster)}
                                        </TableCell>
                                        <TableCell sx={{ width: '28%', wordWrap: 'break-word', whiteSpace: 'pre-wrap', borderColor: '#27272a', py: 0.75 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.35, color: '#d4d4d8', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {cluster['general_overview']}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '8%', borderColor: '#27272a', color: '#fafafa', whiteSpace: 'nowrap' }}>
                                            {formatCountryFieldAsIso(cluster.country)}
                                        </TableCell>
                                        <TableCell sx={{ width: '34%', borderColor: '#27272a', py: 0.75, verticalAlign: 'top', maxWidth: 0 }}>
                                            <RadarArticleTypeChipsRow types={getClusterTypes(cluster)} max={12} layout="wrap" />
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '13%', borderColor: '#27272a', py: 0.5 }}>
                                            <SentimentHistogram articles={cluster.articles} compact />
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '12%', borderColor: '#27272a', color: '#fafafa' }}>
                                            {formatDate(getClusterMaxPublishedAt(cluster))}
                                        </TableCell>
                                        <TableCell align="center" sx={{ width: '3%', borderColor: '#27272a', py: 0.5 }}>
                                            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center" gap={0.25}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openClusterDetailsDrawer(cluster)}
                                                    aria-label="View cluster details"
                                                    sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        handleClusterFeedback(cluster, true);
                                                        setLikedClusters((prev) =>
                                                            prev.includes(cluster.title)
                                                                ? prev.filter((title) => title !== cluster.title)
                                                                : [...prev, cluster.title]
                                                        );
                                                        setDislikedClusters((prev) => prev.filter((title) => title !== cluster.title));
                                                    }}
                                                    sx={{ color: likedClusters.includes(cluster.title) ? '#4ade80' : '#a1a1aa', '&:hover': { color: '#4ade80' } }}
                                                >
                                                    <ThumbUpAltIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        handleClusterFeedback(cluster, false);
                                                        setDislikedClusters((prev) =>
                                                            prev.includes(cluster.title)
                                                                ? prev.filter((title) => title !== cluster.title)
                                                                : [...prev, cluster.title]
                                                        );
                                                        setLikedClusters((prev) => prev.filter((title) => title !== cluster.title));
                                                    }}
                                                    sx={{ color: dislikedClusters.includes(cluster.title) ? '#f87171' : '#a1a1aa', '&:hover': { color: '#f87171' } }}
                                                >
                                                    <ThumbDownAltIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                </Box>

                <Box
                    sx={{
                        position: 'fixed',
                        left: 16,
                        right: 16,
                        bottom: { xs: 20, sm: 28 },
                        zIndex: (theme) => theme.zIndex.appBar,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        gap: 2,
                        pointerEvents: 'none',
                        '& .MuiIconButton-root': { pointerEvents: 'auto' },
                        pr: { xs: 'min(56vw, 240px)', sm: 'min(52vw, 260px)' },
                    }}
                >
                    <Typography sx={{ color: '#fafafa', fontSize: '0.875rem', flexShrink: 0, pb: 0.25 }}>
                        Showing{' '}
                        {radarPage.total === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, radarPage.total)} of {radarPage.total} results
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                        <IconButton
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            sx={{
                                color: currentPage === 1 ? '#52525b' : '#fafafa',
                                '&:hover': { color: '#60a5fa' },
                            }}
                            aria-label="Previous page"
                        >
                            <ChevronLeft />
                        </IconButton>
                        <IconButton
                            onClick={() => setCurrentPage(Math.min(radarPage.pageCount, currentPage + 1))}
                            disabled={radarPage.total === 0 || currentPage >= radarPage.pageCount}
                            sx={{
                                color:
                                    radarPage.total === 0 || currentPage >= radarPage.pageCount
                                        ? '#52525b'
                                        : '#fafafa',
                                '&:hover': { color: '#60a5fa' },
                            }}
                            aria-label="Next page"
                        >
                            <ChevronRight />
                        </IconButton>
                    </Box>
                </Box>

                <Fab
                    variant="extended"
                    disabled={selectedClusters.length === 0}
                    onClick={openSaveToNotebookDrawer}
                    aria-label="Save to notebook"
                    sx={{
                        position: 'fixed',
                        right: { xs: 20, sm: 28 },
                        bottom: { xs: 20, sm: 28 },
                        zIndex: (theme) => theme.zIndex.drawer - 1,
                        bgcolor: '#60a5fa',
                        color: '#0a0a0a',
                        boxShadow: '0 8px 24px rgba(96, 165, 250, 0.35)',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        px: 2,
                        '&:hover': { bgcolor: '#3b82f6' },
                        '&.Mui-disabled': { bgcolor: '#27272a', color: '#52525b', boxShadow: 'none' },
                    }}
                >
                    <SaveIcon sx={{ mr: 1 }} />
                    Save to notebook
                </Fab>

                <Drawer
                    anchor="right"
                    open={radarDrawerMode !== 'closed'}
                    onClose={closeRadarDrawer}
                    PaperProps={{
                        sx: {
                            width: { xs: '100vw', sm: '50vw' },
                            maxWidth: { xs: '100vw', sm: '50vw' },
                            bgcolor: '#18181b',
                            borderLeft: '1px solid #27272a',
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid #27272a',
                                bgcolor: '#09090b',
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 'bold', pr: 1 }}>
                                {radarDrawerMode === 'filters'
                                    ? 'Filters'
                                    : selectedClusterForDetails?.title || 'Cluster'}
                            </Typography>
                            <IconButton onClick={closeRadarDrawer} aria-label="Close" sx={{ color: '#fafafa' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflow: 'auto' }}>
                            {radarDrawerMode === 'filters' && (
                                <Box sx={{ p: 1 }}>
                                    <Accordion
                                        disableGutters
                                        sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                                            <Box display="flex" alignItems="center" gap={1} width="100%">
                                                <Typography fontWeight="bold">Recommended</Typography>
                                                {recommendedFilter && (
                                                    <Chip size="small" label="On" sx={{ height: 22 }} />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={recommendedFilter}
                                                        onChange={(e) => setRecommendedFilter(e.target.checked)}
                                                        sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" sx={{ color: '#d4d4d8' }}>
                                                        Only clusters highlighted in the former “recommended” view (top three per
                                                        local, regional, and world news).
                                                    </Typography>
                                                }
                                            />
                                        </AccordionDetails>
                                    </Accordion>
                                    <Accordion
                                        defaultExpanded
                                        disableGutters
                                        sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                                            <Box display="flex" alignItems="center" gap={1} width="100%">
                                                <Typography fontWeight="bold">Article count</Typography>
                                                {articleCountFilter.length > 0 && (
                                                    <Chip size="small" label={articleCountFilter.length} sx={{ height: 22 }} />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box display="flex" justifyContent="flex-end" mb={1}>
                                                <Button size="small" onClick={() => setArticleCountFilter([])} sx={{ color: '#60a5fa' }}>
                                                    Clear
                                                </Button>
                                            </Box>
                                            <List dense>
                                                {articleCountOptions.map((range) => (
                                                    <ListItem
                                                        key={range}
                                                        dense
                                                        button
                                                        onClick={() => handleArticleCountToggle(range)}
                                                        sx={{ '&:hover': { bgcolor: '#27272a' } }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={articleCountFilter.indexOf(range) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                        />
                                                        <ListItemText primary={range} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                    <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                                <Typography fontWeight="bold">Geography</Typography>
                                                {(geoScopeFilter.length > 0 || countryFilter.length > 0) && (
                                                    <Chip
                                                        size="small"
                                                        label={`${geoScopeFilter.length + countryFilter.length}`}
                                                        sx={{ height: 22 }}
                                                    />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Typography variant="caption" sx={{ color: '#a1a1aa', display: 'block', mb: 1.5 }}>
                                                News scope and countries combine with OR: a row matches if it fits any selected
                                                scope or any selected country.
                                            </Typography>
                                            <Box display="flex" justifyContent="flex-end" gap={1} mb={1} flexWrap="wrap">
                                                <Button
                                                    size="small"
                                                    onClick={() => update({ geoScopes: [], countries: [] })}
                                                    sx={{ color: '#60a5fa' }}
                                                >
                                                    Clear all
                                                </Button>
                                            </Box>
                                            <List dense sx={{ mb: 2 }}>
                                                {(
                                                    [
                                                        { value: 'local' as const, label: 'Local news' },
                                                        { value: 'regional' as const, label: 'Regional news' },
                                                        { value: 'international' as const, label: 'World news' },
                                                    ] as const
                                                ).map(({ value, label }) => (
                                                    <ListItem
                                                        key={value}
                                                        dense
                                                        button
                                                        onClick={() => handleGeoScopeToggle(value)}
                                                        sx={{ '&:hover': { bgcolor: '#27272a' } }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={geoScopeFilter.includes(value)}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                        />
                                                        <ListItemText primary={label} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                                <Typography variant="subtitle2" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
                                                    Countries
                                                </Typography>
                                                <Button size="small" onClick={() => setCountryFilter([])} sx={{ color: '#60a5fa' }}>
                                                    Clear
                                                </Button>
                                            </Box>
                                            <TextField
                                                size="small"
                                                placeholder="Search countries..."
                                                value={countrySearchTerm}
                                                onChange={(e) => setCountrySearchTerm(e.target.value)}
                                                fullWidth
                                                sx={{
                                                    mb: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: '#09090b',
                                                        color: '#fafafa',
                                                        '& fieldset': { borderColor: '#3f3f46' },
                                                    },
                                                }}
                                            />
                                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                                {filteredCountries.map((country) => (
                                                    <ListItem
                                                        key={country}
                                                        dense
                                                        button
                                                        onClick={() => handleCountryToggle(country)}
                                                        sx={{ '&:hover': { bgcolor: '#27272a' } }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={countryFilter.indexOf(country) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                        />
                                                        <ListItemText primary={country} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                    <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontWeight="bold">Format</Typography>
                                                {entryTypeFilter.length > 0 && (
                                                    <Chip size="small" label={entryTypeFilter.length} sx={{ height: 22 }} />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box display="flex" justifyContent="flex-end" mb={1}>
                                                <Button size="small" onClick={() => setEntryTypeFilter([])} sx={{ color: '#60a5fa' }}>
                                                    Clear
                                                </Button>
                                            </Box>
                                            <TextField
                                                size="small"
                                                placeholder="Search formats..."
                                                value={entryTypeSearchTerm}
                                                onChange={(e) => setEntryTypeSearchTerm(e.target.value)}
                                                fullWidth
                                                sx={{
                                                    mb: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: '#09090b',
                                                        color: '#fafafa',
                                                        '& fieldset': { borderColor: '#3f3f46' },
                                                    },
                                                }}
                                            />
                                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                                {filteredEntryTypes.map((type) => (
                                                    <ListItem
                                                        key={type}
                                                        dense
                                                        button
                                                        onClick={() => handleEntryTypeToggle(type)}
                                                        sx={{ '&:hover': { bgcolor: '#27272a' } }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={entryTypeFilter.indexOf(type) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                        />
                                                        <ListItemText primary={type} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                    <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontWeight="bold">Category</Typography>
                                                {contentCategoryFilter.length > 0 && (
                                                    <Chip size="small" label={contentCategoryFilter.length} sx={{ height: 22 }} />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box display="flex" justifyContent="flex-end" mb={1}>
                                                <Button size="small" onClick={() => setContentCategoryFilter([])} sx={{ color: '#60a5fa' }}>
                                                    Clear
                                                </Button>
                                            </Box>
                                            <TextField
                                                size="small"
                                                placeholder="Search categories..."
                                                value={contentCategorySearchTerm}
                                                onChange={(e) => setContentCategorySearchTerm(e.target.value)}
                                                fullWidth
                                                sx={{
                                                    mb: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: '#09090b',
                                                        color: '#fafafa',
                                                        '& fieldset': { borderColor: '#3f3f46' },
                                                    },
                                                }}
                                            />
                                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                                {filteredContentCategories.map((category) => (
                                                    <ListItem
                                                        key={category}
                                                        dense
                                                        button
                                                        onClick={() => handleContentCategoryToggle(category)}
                                                        sx={{ '&:hover': { bgcolor: '#27272a' } }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={contentCategoryFilter.indexOf(category) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                        />
                                                        <ListItemText primary={category} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                    <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography fontWeight="bold">SDG topic</Typography>
                                                {issueTypeFilter.length > 0 && (
                                                    <Chip size="small" label={issueTypeFilter.length} sx={{ height: 22 }} />
                                                )}
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box display="flex" justifyContent="flex-end" mb={1}>
                                                <Button size="small" onClick={() => setIssueTypeFilter([])} sx={{ color: '#60a5fa' }}>
                                                    Clear
                                                </Button>
                                            </Box>
                                            <TextField
                                                size="small"
                                                placeholder="Search SDG topics..."
                                                value={issueTypeSearchTerm}
                                                onChange={(e) => setIssueTypeSearchTerm(e.target.value)}
                                                fullWidth
                                                sx={{
                                                    mb: 1,
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: '#09090b',
                                                        color: '#fafafa',
                                                        '& fieldset': { borderColor: '#3f3f46' },
                                                    },
                                                }}
                                            />
                                            <List dense sx={{ maxHeight: 240, overflow: 'auto' }}>
                                                {filteredIssueTypes.map((type) => (
                                                    <ListItem
                                                        key={type}
                                                        dense
                                                        button
                                                        onClick={() => handleIssueTypeToggle(type)}
                                                        sx={{ '&:hover': { bgcolor: '#27272a' } }}
                                                    >
                                                        <Checkbox
                                                            edge="start"
                                                            checked={issueTypeFilter.indexOf(type) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                                                        />
                                                        <ListItemText primary={type} />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            )}
                            {radarDrawerMode === 'details' && selectedClusterForDetails && (
                                <ClusterRadarDetailPanel
                                    cluster={selectedClusterForDetails}
                                    onArticleFeedback={handleArticleFeedback}
                                    incorrectArticles={incorrectArticles}
                                />
                            )}
                        </Box>
                    </Box>
                </Drawer>

                <Snackbar
                    open={missingClusterToastOpen}
                    autoHideDuration={6000}
                    onClose={() => setMissingClusterToastOpen(false)}
                    TransitionComponent={Collapse}
                >
                    <Alert onClose={() => setMissingClusterToastOpen(false)} severity="info" sx={{ width: '100%' }}>
                        That cluster wasn't found in the current results. It may belong to another analytic profile, or it
                        is no longer in the Radar window.
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={clusterLoadFailedToastOpen}
                    autoHideDuration={6000}
                    onClose={() => setClusterLoadFailedToastOpen(false)}
                    TransitionComponent={Collapse}
                >
                    <Alert onClose={() => setClusterLoadFailedToastOpen(false)} severity="error" sx={{ width: '100%' }}>
                        Couldn't load that cluster. Reload the page to try again.
                    </Alert>
                </Snackbar>

                {/* Snackbar for cluster feedback success/error */}
                <Snackbar
                    open={showClusterFeedbackAlert}
                    autoHideDuration={6000}
                    onClose={() => setShowClusterFeedbackAlert(false)}
                    TransitionComponent={Collapse}
                >
                    {clusterFeedbackError ? (
                        <Alert onClose={() => setShowClusterFeedbackAlert(false)} severity="error" sx={{width: '100%'}}>
                            {clusterFeedbackError}
                        </Alert>
                    ) : clusterFeedbackSuccess ? (
                        <Alert onClose={() => setShowClusterFeedbackAlert(false)} severity="success" sx={{width: '100%'}}>
                            {clusterFeedbackSuccess}
                        </Alert>
                    ) : <></>}
                </Snackbar>

                {/* Snackbar for article feedback success/error */}
                <Snackbar
                    open={showArticleFeedbackAlert}
                    autoHideDuration={6000}
                    onClose={() => setShowArticleFeedbackAlert(false)}
                >
                    {articleFeedbackError ? (
                        <Alert onClose={() => setShowArticleFeedbackAlert(false)} severity="error" sx={{ width: '100%' }}>
                            {articleFeedbackError}
                        </Alert>
                    ) : articleFeedbackSuccess ? (
                        <Alert onClose={() => setShowArticleFeedbackAlert(false)} severity="success" sx={{ width: '100%' }}>
                            {articleFeedbackSuccess}
                        </Alert>
                    ) : <></>}
                </Snackbar>

                <Drawer
                    anchor="right"
                    open={saveToNotebookDrawerOpen}
                    onClose={closeSaveToNotebookDrawer}
                    PaperProps={{
                        sx: {
                            width: { xs: '100vw', sm: '50vw' },
                            maxWidth: { xs: '100vw', sm: '50vw' },
                            bgcolor: '#18181b',
                            borderLeft: '1px solid #27272a',
                            zIndex: (theme) => theme.zIndex.drawer + 2,
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid #27272a',
                                bgcolor: '#09090b',
                                flexShrink: 0,
                            }}
                        >
                            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
                                Add to notebook
                            </Typography>
                            <IconButton onClick={closeSaveToNotebookDrawer} aria-label="Close" sx={{ color: '#fafafa' }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                p: 2,
                                overflow: 'hidden',
                            }}
                        >
                            {selectedClusters.length > MAX_CLUSTERS_PER_SAVE && (
                                <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
                                    You can only add up to {MAX_CLUSTERS_PER_SAVE} clusters to a notebook at once. Please
                                    deselect some clusters before saving.
                                </Alert>
                            )}
                            <Tabs
                                value={saveNotebookTab}
                                onChange={(_, v) => setSaveNotebookTab(v)}
                                sx={{
                                    mb: 2,
                                    flexShrink: 0,
                                    borderBottom: '1px solid #27272a',
                                    '& .MuiTab-root': { color: '#a1a1aa', textTransform: 'none' },
                                    '& .Mui-selected': { color: '#fafafa' },
                                    '& .MuiTabs-indicator': { bgcolor: '#60a5fa' },
                                }}
                            >
                                <Tab label="Existing notebook" />
                                <Tab label="New notebook" />
                            </Tabs>
                            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {saveNotebookTab === 0 ? (
                                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                        <NotebookPickerList
                                            notebooks={activeNotebooks}
                                            selectedId={selectedNotebookId}
                                            onSelect={setSelectedNotebookId}
                                            loading={notebooksLoading}
                                            emptyMessage={
                                                <>
                                                    No active notebooks. Switch to &quot;New notebook&quot; to create one.
                                                </>
                                            }
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label="Notebook name"
                                            fullWidth
                                            value={newNotebookName}
                                            onChange={(e) => setNewNotebookName(e.target.value)}
                                            sx={{
                                                flexShrink: 0,
                                                '& .MuiOutlinedInput-root': { color: '#fafafa' },
                                                '& .MuiInputLabel-root': { color: '#a1a1aa' },
                                            }}
                                        />
                                        <TextField
                                            label="Description (optional)"
                                            fullWidth
                                            multiline
                                            value={newNotebookDescription}
                                            onChange={(e) => setNewNotebookDescription(e.target.value)}
                                            sx={{
                                                flex: 1,
                                                minHeight: 0,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                                '& .MuiInputBase-root': {
                                                    flex: 1,
                                                    alignItems: 'flex-start',
                                                    py: 1,
                                                },
                                                '& textarea': {
                                                    height: '100% !important',
                                                    overflow: 'auto !important',
                                                    boxSizing: 'border-box',
                                                },
                                                '& .MuiOutlinedInput-root': { color: '#fafafa', height: '100%' },
                                                '& .MuiInputLabel-root': { color: '#a1a1aa' },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                flexShrink: 0,
                                px: 2,
                                py: 2,
                                borderTop: '1px solid #27272a',
                                bgcolor: '#18181b',
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={() => void handleSaveToNotebook()}
                                disabled={isSavingToNotebook || selectedClusters.length > MAX_CLUSTERS_PER_SAVE}
                                startIcon={<SaveIcon />}
                                sx={{ bgcolor: '#60a5fa', color: '#0a0a0a', '&:hover': { bgcolor: '#3b82f6' } }}
                            >
                                {isSavingToNotebook ? 'Saving…' : 'Save'}
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                {/* Snackbar for save to notebook feedback */}
                <Snackbar
                    open={showSaveToNotebookAlert}
                    autoHideDuration={6000}
                    onClose={() => setShowSaveToNotebookAlert(false)}
                    TransitionComponent={Collapse}
                >
                    {saveToNotebookError ? (
                        <Alert onClose={() => setShowSaveToNotebookAlert(false)} severity="error" sx={{width: '100%'}}>
                            {saveToNotebookError}
                        </Alert>
                    ) : saveToNotebookSuccess ? (
                        <Alert onClose={() => setShowSaveToNotebookAlert(false)} severity="success" sx={{width: '100%'}}>
                            {saveToNotebookSuccess}
                        </Alert>
                    ) : <></>}
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default LayoutNewsClusters;
